import { createStandaloneToast } from '@chakra-ui/react';
import { boundAdminGetRequest } from '../../shared/utils/adminGetBudget';
import theme from '../../shared/styles/theme';
import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { API_URL } from '../../shared/constants';
import { AccessRoot, permits, refreshAccess } from '../../shared/access/access';
import { endpointAccess } from '../../shared/access/endpointAccess';
import { ACCESS_WRITE_WAIT_MS, waitForProjection, writeAccessError } from '../../shared/access/writeAccess';

const mainBaseUrl = '/v2';
// Same theme and position as the app's own toasts (ChakraProvider in App.tsx).
const budgetToast = createStandaloneToast({
  theme,
  // Passing defaultOptions replaces Chakra's defaults, so keep its solid variant.
  defaultOptions: { position: 'top-right', variant: 'solid' },
});

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  credentials: 'include',
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const requirement = endpointAccess[api.endpoint];
  const origin = (api.getState() as AccessRoot).auth;
  let generation = origin?.generation;
  const method = typeof args === 'string' ? 'GET' : (args.method || 'GET').toUpperCase();
  const write = requirement !== 'session' && (api.type === 'mutation' || !['GET', 'HEAD'].includes(method));
  if (!requirement || (requirement !== 'session' && !permits(api.getState() as AccessRoot, requirement))) {
    return { error: writeAccessError('ACTION_UNAVAILABLE') };
  }
  const send = async (recheckIdentity = false) => {
    if (requirement !== 'session' && !permits(api.getState() as AccessRoot, requirement)) {
      return { error: writeAccessError('ACTION_UNAVAILABLE') };
    }
    if (write) {
      if (recheckIdentity) api.dispatch(refreshAccess());
      const deadline = Date.now() + ACCESS_WRITE_WAIT_MS;
      let auth = (api.getState() as AccessRoot).auth;
      while (auth?.accessStatus === 'refreshing') {
        if (auth.profile?.userId !== origin?.profile?.userId) return { error: writeAccessError('ACCESS_IDENTITY_CHANGED') };
        const failure = await waitForProjection(api.dispatch(refreshAccess()), api.signal, deadline - Date.now());
        if (failure) return { error: writeAccessError(failure) };
        auth = (api.getState() as AccessRoot).auth;
      }
      if (!auth || auth.accessStatus !== 'ready') return { error: writeAccessError('ACCESS_CHECK_FAILED') };
      if (!origin?.profile?.userId || auth.profile?.userId !== origin.profile.userId) return { error: writeAccessError('ACCESS_IDENTITY_CHANGED') };
      if (!permits({ auth }, requirement)) return { error: writeAccessError('ACTION_UNAVAILABLE') };
      if (api.signal.aborted) return { error: writeAccessError('ACCESS_WRITE_CANCELLED') };
      generation = auth.generation;
    }
    // No await between the final identity/permission check and transport.
    const bounded = boundAdminGetRequest(typeof args === 'string' ? { url: args } : args);
    if (bounded.error) {
      if (!budgetToast.isActive('admin-get-byte-budget')) budgetToast({
        id: 'admin-get-byte-budget',
        title: bounded.error,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      return { error: { status: 'CUSTOM_ERROR' as const, error: bounded.error } };
    }
    return baseQuery(bounded.request!, api, extraOptions);
  };
  let result = await send();
  if (result.error?.status === 'CUSTOM_ERROR') return result;
  if ((api.getState() as AccessRoot).auth?.generation !== generation) {
    return { error: { status: 'CUSTOM_ERROR', error: 'ACCESS_CHANGED' } };
  }
  if (result.error) {
    if (result.error.status === 401) {
      const refreshResult = await baseQuery(
        { url: `${mainBaseUrl}/profiles/refresh_token`, method: 'POST' },
        api,
        extraOptions,
      );
      if ((api.getState() as AccessRoot).auth?.generation !== generation || api.signal.aborted) {
        return { error: { status: 'CUSTOM_ERROR', error: 'ACCESS_CHANGED' } };
      }
      if (refreshResult?.meta?.response?.status === 200) {
        result = await send(write);
        if (result.error?.status === 'CUSTOM_ERROR') return result;
      } else {
        await baseQuery(
          { url: `${mainBaseUrl}/profiles/logout`, method: 'POST' },
          api,
          extraOptions,
        );
      }
    }
  }

  if ((api.getState() as AccessRoot).auth?.generation !== generation) {
    return { error: { status: 'CUSTOM_ERROR', error: 'ACCESS_CHANGED' } };
  }

  if (result.error?.status === 403 && api.endpoint !== 'getCurrentUserProfile') api.dispatch(refreshAccess());
  return { ...result, meta: { ...('meta' in result ? result.meta : undefined), accessGeneration: generation } };
};

export const baseApi = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Auth',
    'Company',
    'Profile',
    'Tag',
    'MLTag',
    'Delivery',
    'Usage',
    'Template',
    'EmailNotification',
    'PricingPlan',
    'BillingAccount',
    'SignupRequest',
    'DeletionRequest',
    'IntegrationDefinitions',
    'TagTranslation',
    'DashboardShareToken',
    'PlatformRoles'
  ],
  endpoints: () => ({}),
});
